import type { SuperHubAPIDefinitionSlice } from './ApiDefinitions';
import type { SuperHubBranchesSlice } from './Branches';
import type { SuperHubDocumentSlice } from './Document';
import type { SuperHubEditorSlice } from './Editor';
import type { SuperHubLinterSlice } from './Linter';
import type { SuperHubSidebarSlice } from './Sidebar';
import type { SuperHubVersionSlice } from './Versions';
import type { BranchRepresentationType } from '@readme/api/src/mappings/branch/types';
import type { VersionRepresentationType } from '@readme/api/src/mappings/version/types';
import type { ProjectClientSide } from '@readme/backend/models/project/types';
import type { I18nLanguage } from '@readme/iso';

import { matchPath } from 'react-router-dom';

import { projectStore } from '@core/store/Project';

import { SuperHubHashRoutePaths, SuperHubRoutePaths } from '@routes/SuperHub/types';
import type { SuperHubHashRouteParams, SuperHubRouteParams } from '@routes/SuperHub/types';

import { actionLog, createBoundedUseStore, isClient } from '../util';
import { createCrossFrameStore } from '../util/createCrossFrameStore';

import { createSuperHubAPIDefinitionSlice } from './ApiDefinitions';
import { createSuperHubBranchesSlice } from './Branches';
import { createSuperHubDocumentSlice } from './Document';
import { createSuperHubEditorSlice } from './Editor';
import { createSuperHubLinterSlice } from './Linter';
import { createSuperHubSidebarSlice } from './Sidebar';
import { isBranch, isVersion } from './util';
import { createSuperHubVersionSlice } from './Versions';
import { createDefaultVersion } from './Versions/defaults';

interface SlimProjectObj {
  id: string;
  name: string;
  subdomain: string;
}

interface SuperHubStoreState {
  /**
   * Current project version or branch that is in view, e.g. the `1.0` version
   * object or `1.0_my-branch` branch object.
   */
  currentBranch: BranchRepresentationType | VersionRepresentationType;

  /**
   * Default SuperHub options object for the useReadmeApi hook
   */
  defaultApiOptions: { useLegacyBaseUrl: boolean };

  /**
   * When current project is an Enterprise project, this field becomes an object
   * populated with additional enterprise-related info. Otherwise, it remains
   * `undefined` for non-enterprise projects.
   */
  enterprise?: {
    /**
     * Whether the current project is a child of an Enterprise group.
     */
    isChild: boolean;
    /**
     * Whether the current project is an Enterprise group.
     */
    isGroup: boolean;
    /**
     * Whether the Single Project Enterprise flag is enabled.
     * (The child inherits this flag from the parent group.)
     */
    isSingleProject: boolean;
    /**
     * The current enterprise group project object.
     */
    parent: SlimProjectObj | undefined;
    /**
     * List of all children projects in the Enterprise group,
     * including the current project itself.
     */
    projects: SlimProjectObj[];
  };

  /**
   * Whether or not the SuperHub admin UI is temporarily hidden.
   */
  isAdminUIHidden: boolean;

  /**
   * Indicates whether SuperHub should allow access to bidirectional sync
   */
  isBidiSync: boolean;

  /**
   * True when pages goes into "edit" mode.
   */
  isEditing: boolean;

  /**
   * Whether git-backed internationalization (i18n) is enabled.
   */
  isI18nEnabled: boolean;

  /**
   * Whether or not the JumpBox is currently open (defaults to false).
   */
  isJumpBoxOpen: boolean;

  /**
   * Whether or not My Developers panel is expanded (defaults to true).
   */
  isMyDevPanelExpanded: boolean;

  /**
   * Whether or not Owlbot chat sidebar is open (defaults to false).
   */
  isOwlbotOpenCached: boolean;

  /**
   * Indicates that the store has been initialized to its beginning state via
   * `initialize()` action and lets connected subscribers know that it is ready
   * for consumption.
   * @see initialize
   */
  isReady: boolean;

  /**
   * Indicates whether the current route is on the API definitions list view
   * within the Reference section. Mainly used as a convenience that inspects
   * route segments to determine which reference section view to load.
   */
  isReferenceDefinitionsRoute: boolean;

  /**
   * Indicates whether SuperHub is enabled for the current project. Will be
   * `true` for all user roles including both admins and logged-out users.
   */
  isSuperHub: boolean;

  /**
   * Indicates this project is a child project of an parent enterprise group
   * that has merge rules enabled.
   */
  isSuperHubBranchMergeRulesEnabled: boolean;

  /**
   * Lang short code derived from the current branch or version.
   */
  language: I18nLanguage;

  /**
   * Determines which layout to render the SuperHub in.
   * - `default`: Default layout for the SuperHub.
   * - `aside`: Used when comparing two documents side-by-side.
   * - `aside-standalone`: Same as `aside` but without a top nav bar.
   * - `edit`: Used when creating, updating or reviewing a document.
   * - `my-developers`: Used when viewing My Developers panels.
   * - `settings`: Used when configuring settings. The Hub content is hidden in this layout.
   * - `settings-preview`: Used when configuring settings side-by-side with the Hub content.
   * - `settings-preview-stretch`: Used when configuring settings side-by-side for custom code layouts.
   * - `versions`: Used when managing versions.
   * - `ai`: Used when accessing AI features.
   */
  layout:
    | 'ai'
    | 'aside-standalone'
    | 'aside'
    | 'default'
    | 'docs-audit'
    | 'edit'
    | 'my-developers'
    | 'settings-preview-stretch'
    | 'settings-preview'
    | 'settings'
    | 'versions';

  /**
   * Stores the previously active hash route when the admin UI is hidden so we can restore it.
   */
  previousAdminUIHash: string | null;

  /**
   * Refers to the top-level route action that is being performed on any given
   * section. For example, when navigating to `/update/reference/pet`, the
   * action is `update`, which indicates we are editing a Reference page with the
   * slug named `pet`.
   */
  routeAction: NonNullable<SuperHubRouteParams['action']> | null;

  /**
   * Refers to the top-level route section that is being viewed on the Hub such
   * as `docs`, `reference`, `changelog`, etc. For example. when navigating to
   * `/reference/pet`, the top-level route section is `reference`. This is
   * typically but not always the first segment of the route URL.
   */
  routeSection: NonNullable<SuperHubRouteParams['section']> | null;

  /**
   * Whether or not preview mode is enabled; and whether it should be expanded
   * (1440px aka container) or fit to the available space. Defaults to `fit`.
   */
  settingsPreviewMode?: 'expand' | 'fit';

  /**
   * Contains the encoded page slug from the hub route that is currently being
   * viewed. For example, when viewing `/docs/getting-started`, the slug is
   * `getting-started`, typically the second segment of the hub route.
   * @example
   * ```ts
   * 'getting-started'
   * 'category%20page'
   * ```
   */
  slug: string | null;

  /**
   * Project subdomain that is currently in view, e.g. `my-project-slug`
   */
  subdomain: string | null;

  /** Info about the current user whether they are logged in or not. */
  user: {
    /** Logged in user's email, e.g. `banjo@kazooie.com`. */
    email: string | null;
    /** Logged in user's ID, e.g. `user.teammateUserId`. */
    id: string | null;
    /** Whether the user is authenticated on password-protected projects. */
    isAuthenticated: boolean;
    /** Whether the user is an Enterprise group admin. */
    isGroupAdmin: boolean;
    /** Whether the user is the owner of the project. */
    isOwner: boolean;
    /** Logged in user's name, e.g. `Banjo Stevens`. */
    name: string | null;
    /**
     * Indicates the current user's role and what features they have access to.
     * - `admin`: full access to project settings and content creating.
     * - `editor`: limited access to project settings and content editing.
     * - `viewer`: logged-out users with view-only access to content.
     */
    role: 'admin' | 'editor' | 'viewer';
    /** User's Stripe customer ID. */
    stripeId: string | null;
  };
}

interface SuperHubStoreAction {
  /**
   * Generate the API endpoint URL with the provided `subdomain` and/or `branch`
   * name. If no argument is provided, it defaults to values that currently
   * exist in the store.
   *
   * To exclude the branch name, set `branch` to an empty string.
   * To exclude the entire branch segment from the URL, set `branch` to `null`.
   *
   * @example
   * ```ts
   * getApiBaseUrl()
   *   => '/subdomain/api-next/v2/branches/1.0'
   * getApiBaseUrl({ subdomain: 'custom', branch: '2.0_branch' })
   *   => '/custom/api-next/v2/branches/2.0_branch'
   * getApiBaseUrl({ branch: '' })
   *   => '/subdomain/api-next/v2/branches'
   * getApiBaseUrl({ branch: null })
   *   => '/subdomain/api-next/v2'
   * ```
   */
  getApiBaseUrl: (args?: {
    subdomain?: SuperHubStoreState['subdomain'];
    branch?: SuperHubStoreState['currentBranch']['name'] | null;
  }) => string;

  /**
   * Returns the branch object that is `currentBranch` when it's populated with
   * a child branch and not a version. Otherwise returns `null`. Use this when
   * you specifically need to work with child branches and not versions.
   * @example
   * ```ts
   * const currentChildBranch = useSuperHubStore(s => getCurrentChildBranch());
   * if (!currentChildBranch) {
   *   // We're not on a branch, so don't try to access branch-only properties.
   *   return;
   * }
   * ```
   */
  getCurrentChildBranch: () => BranchRepresentationType | null;

  /**
   * Returns a version object if `currentBranch` is populated with a version.
   * Otherwise, returns the `currentBranch`'s parent (aka `base`) version. Use
   * this when you specifically need to work with versions and not branches.
   *
   * ⚠️ Note that this should only be used for functionality that is
   * intentionally only available for versions and not branches (e.g. Metrics).
   * @example
   * ```ts
   * const currentVersion = useSuperHubStore(s => getCurrentVersion());
   * const metricsFetch = useMetricsAPIFetcher({
   *   version: currentVersion.name,
   * });
   * ```
   */
  getCurrentVersion: () => VersionRepresentationType;

  /**
   * Initializes the store based on the provided settings. In order to enable
   * SSR, this needs to be called inline and high up in the rendering tree to
   * make the store's state ready before rendering components downstream during
   * both SSR and the initial CSR.
   */
  initialize: (settings: {
    /**
     * The full git-backed representation of the current version or branch. This
     * value is only available for git-backed projects and will be `undefined`
     * for legacy projects.
     */
    currentBranch?: SuperHubStoreState['currentBranch'];

    language?: SuperHubStoreState['language'];

    /** Whether the current project is a child of an Enterprise group. */
    isEnterpriseChild?: boolean;

    /** Whether the current project is an Enterprise group. */
    isEnterpriseGroup?: boolean;

    /** Enterprise group parent object. */
    enterpriseParent?: SlimProjectObj;

    /** List of sibling projects in current Enterprise group.  */
    enterpriseProjects?: SlimProjectObj[];

    /**
     * Browser location pathname and hash to initialize the store to.
     * @example
     * ['/docs/getting-started', '#/settings/appearance/theme-editor']
     */
    location?: [pathname: string, hash?: string];

    /**
     * Project feature flags that are relevant to SuperHub in order to determine
     * whether it should be enabled or not.
     */
    projectFlags: Partial<ProjectClientSide['flags']>;

    /**
     * Project subdomain.
     * @example my-project
     */
    subdomain: SuperHubStoreState['subdomain'];

    /** Info about the currently logged in user. */
    user?: SuperHubStoreState['user'];
  }) => void;

  /**
   * Resets the entire store back to its initial default state.
   */
  reset: () => void;

  /**
   * Toggles the `isAdminUIHidden` state.
   */
  toggleAdminUIHidden: (nextState?: boolean) => void;

  /**
   * Toggles the `isJumpBoxOpen` state.
   */
  toggleJumpBox: (nextState?: boolean) => void;

  /**
   * Toggles the `isOwlbotOpenCached` state.
   */
  toggleOwlbot: () => void;

  /**
   * Toggles the `isMyDevPanelExpanded` state.
   */
  updateIsMyDevPanelExpanded: () => void;

  /**
   * Updates the language property.
   */
  updateLanguage: (language: I18nLanguage) => void;

  /**
   * Updates the current route we've currently navigated to with the provided
   * URL pathname and hash properties. Segments in each property are then parsed
   * and interpreted by our state.
   * @see ConnectSuperHubStoreToRouter
   */
  updateRoute: (pathname: string, hash?: string) => void;

  /**
   * Toggles the `preview` state.
   */
  updateSettingsPreviewMode: (settingsPreviewMode?: 'expand' | 'fit') => void;
}

export type SuperHubStore = SuperHubAPIDefinitionSlice &
  SuperHubBranchesSlice &
  SuperHubDocumentSlice &
  SuperHubEditorSlice &
  SuperHubLinterSlice &
  SuperHubSidebarSlice &
  SuperHubStoreAction &
  SuperHubStoreState &
  SuperHubVersionSlice;

const initialState: SuperHubStoreState = {
  currentBranch: {
    // Default our initial branch to `stable` to cover cases like for enterprise
    // groups where they are technically version-less.
    ...createDefaultVersion({ name: 'stable' }),
    source: 'readme',
    updated_at: '',
    uri: '/branches/stable',
  },
  language: 'en',
  defaultApiOptions: {
    useLegacyBaseUrl: false,
  },
  enterprise: undefined,
  isBidiSync: false,
  isEditing: false,
  isAdminUIHidden: false,
  previousAdminUIHash: null,
  isI18nEnabled: false,
  isMyDevPanelExpanded: true,
  isOwlbotOpenCached: false,
  isJumpBoxOpen: false,
  isReady: false,
  isReferenceDefinitionsRoute: false,
  isSuperHub: false,
  isSuperHubBranchMergeRulesEnabled: false,
  layout: 'default',
  routeAction: null,
  routeSection: null,
  settingsPreviewMode: 'fit',
  slug: null,
  subdomain: null,
  user: {
    email: null,
    id: null,
    isAuthenticated: false,
    isGroupAdmin: false,
    isOwner: false,
    name: null,
    role: 'viewer',
    stripeId: null,
  },
};

/**
 * Vanilla store that contains all application-level state data required by
 * SuperHub. This store can be accessed and used anywhere. React components
 * should call `useSuperHubStore()` instead.
 * @example
 * import { superHubStore } from '@core/store';
 *
 * const isEditing = superHubStore.getState().isEditing;
 */
export const superHubStore = createCrossFrameStore<SuperHubStore>('SuperHubStore', (set, get, ...props) => {
  /**
   * Holds reference to the initial state so we can support resetting the
   * store back to this state when calling `reset()`.
   */
  const resetState = {
    ...createSuperHubAPIDefinitionSlice(set, get, ...props),
    ...createSuperHubDocumentSlice(set, get, ...props),
    ...createSuperHubEditorSlice(set, get, ...props),
    ...createSuperHubSidebarSlice(set, get, ...props),
    ...createSuperHubVersionSlice(set, get, ...props),
    ...createSuperHubBranchesSlice(set, get, ...props),
    ...createSuperHubLinterSlice(set, get, ...props),
    ...initialState,
  };

  return {
    ...resetState,

    getApiBaseUrl: (args = {}) => {
      const { subdomain: currentSubdomain, currentBranch } = get();
      const subdomain = args.subdomain || currentSubdomain;
      if (args.branch === null) {
        return `/${subdomain}/api-next/v2`;
      } else if (args.branch === '') {
        return `/${subdomain}/api-next/v2/branches`;
      }

      const branch = args.branch || currentBranch.name;
      return `/${subdomain}/api-next/v2/branches/${encodeURIComponent(branch)}`;
    },

    getCurrentChildBranch: () => {
      const { currentBranch } = get();
      return isBranch(currentBranch) ? currentBranch : null;
    },

    getCurrentVersion: () => {
      const { currentBranch } = get();
      return isVersion(currentBranch) ? currentBranch : currentBranch.base;
    },

    initialize: settings => {
      // When store is initializing during SSR, we have to explicitly reset
      // it to its initial state. Otherwise, there could be remnants of
      // stale data leftover in the store from a previous SSR. This is b/c
      // our store is a singleton and never gets destroyed on the server.
      if (!isClient) get().reset();

      const {
        currentBranch,
        language,
        isEnterpriseChild,
        isEnterpriseGroup,
        enterpriseParent,
        enterpriseProjects = [],
        location = [],
        projectFlags: flags,
        subdomain,
        user,
      } = settings;
      set(
        state => {
          if (currentBranch) {
            state.currentBranch = currentBranch;
          }
          state.language = language || 'en';

          state.isReady = isClient;
          state.isSuperHub = !!flags.superHub;
          state.isI18nEnabled =
            state.isSuperHub && !!flags.gitTranslations && projectStore.getState().data.i18n.state === 'enabled';
          state.isSuperHubBranchMergeRulesEnabled = state.isSuperHub && !!flags.superHubBranchMergeRules;
          state.isBidiSync = state.isSuperHub && user?.role === 'admin' && !!flags.bidiSync;
          state.subdomain = subdomain;
          state.user = {
            email: user?.email || null,
            id: user?.id || null,
            isAuthenticated: user?.isAuthenticated || false,
            isGroupAdmin: user?.isGroupAdmin || false,
            isOwner: user?.isOwner || false,
            name: user?.name || null,
            role: state.isSuperHub && user ? user.role : 'viewer',
            stripeId: user?.stripeId || null,
          };

          if (isClient && window.localStorage) {
            const savedOwlbotState = window.localStorage.getItem('owlbot-open');
            if (savedOwlbotState !== null) {
              state.isOwlbotOpenCached = savedOwlbotState === 'true';
            }
          }

          if (isEnterpriseGroup || isEnterpriseChild) {
            state.enterprise = {
              isGroup: !!isEnterpriseGroup,
              isChild: !!isEnterpriseChild,
              isSingleProject: 'singleProjectEnterprise' in flags && !!flags.singleProjectEnterprise,
              parent: enterpriseParent,
              projects: enterpriseProjects,
            };
          }
        },
        false,
        actionLog('initialize', settings),
      );

      // Continue initialization based on the location route.
      const [pathname, hash] = location;
      if (pathname) {
        get().updateRoute(pathname, hash);
      }
    },

    updateLanguage: language => {
      set({ language }, false, actionLog('updateLanguage', language));
      get().versions.updateI18nFilteredData();
    },

    reset: () => {
      set(resetState, false, actionLog('reset'));
      get().sidebar.updateCache();
    },

    toggleAdminUIHidden: nextState => {
      if (typeof window === 'undefined') return;

      const { isAdminUIHidden, previousAdminUIHash, isEditing } = get();
      const shouldHide = nextState ?? !isAdminUIHidden;

      // Prevent hiding UI while editing
      if (shouldHide && isEditing) return;

      const currentHash = window.location.hash;
      const isMeaningfulHash = currentHash && currentHash !== '#/' && currentHash !== '';

      set(
        state => {
          state.isAdminUIHidden = shouldHide;
          if (shouldHide) {
            state.isJumpBoxOpen = false;
            state.previousAdminUIHash = isMeaningfulHash ? currentHash : null;
          } else {
            state.previousAdminUIHash = null;
          }
        },
        false,
        actionLog('toggleAdminUIHidden', nextState),
      );

      // Update the URL hash
      if (shouldHide) {
        window.location.hash = '/';
      } else if (previousAdminUIHash && previousAdminUIHash !== '#/') {
        window.location.hash = previousAdminUIHash;
      }
    },

    toggleJumpBox: nextState => {
      set(
        state => {
          state.isJumpBoxOpen = nextState ?? !state.isJumpBoxOpen;
        },
        false,
        actionLog('toggleJumpBox', nextState),
      );
    },

    updateIsMyDevPanelExpanded: () => {
      set(
        state => {
          state.isMyDevPanelExpanded = !state.isMyDevPanelExpanded;
        },
        false,
        actionLog('updateIsMyDevPanelExpanded'),
      );
    },

    toggleOwlbot: () => {
      set(
        state => {
          state.isOwlbotOpenCached = !state.isOwlbotOpenCached;

          if (typeof window !== 'undefined' && window.localStorage) {
            window.localStorage.setItem('owlbot-open', state.isOwlbotOpenCached ? 'true' : 'false');
          }
        },
        false,
        actionLog('toggleOwlbot'),
      );
    },

    updateSettingsPreviewMode: (settingsPreviewMode = 'fit') => {
      set(
        state => {
          state.settingsPreviewMode = settingsPreviewMode;
        },
        false,
        actionLog('updateSettingsPreviewMode', { settingsPreviewMode }),
      );
    },

    updateRoute: (pathname, hash = '') => {
      // Match the same route paths from our superhub routers to parse out
      // route params in the same way.
      const match = matchPath<SuperHubRouteParams>(pathname, Object.values(SuperHubRoutePaths));
      const hashMatch = matchPath<SuperHubHashRouteParams>(hash.substring(1), Object.values(SuperHubHashRoutePaths));

      const {
        params: { action = null, section = pathname === '/' ? 'home' : null, slug = null },
      } = match ?? { params: {} };
      const {
        params: { action: hashAction, section: hashSection },
      } = hashMatch ?? { params: {} };

      set(
        state => {
          state.isEditing = ['create', 'update', 'compare'].includes(action || '');
          state.isReferenceDefinitionsRoute = action === 'update' && section === 'reference' && !slug;
          state.routeAction = action;
          state.routeSection = section;
          // React router's `useLocation` hook returns a decoded pathname that
          // gets parsed into decoded params, but we want to preserve params in
          // their original encoded form. This makes it easier to match slugs to
          // the encoded slugs returned from our API. If this behavior ever
          // changes however, avoid double-encoding an already encoded slug by
          // first decoding it, then re-encoding.
          state.slug = slug ? encodeURIComponent(decodeURIComponent(slug)) : null;
          state.editor.isCreateNewPage = action === 'create';

          // ⚠️ Be mindful of order when updating!! Hash actions should be
          // evaluated before actions, to ensure that hash action based
          // panel overlays take precedense over the main action
          if (action === 'compare') {
            state.layout = 'aside-standalone';
          } else if (hashAction === 'versions') {
            state.layout = 'versions';
          } else if (hashAction === 'my-developers') {
            state.layout = 'my-developers';
          } else if (hashAction === 'settings' || hashAction === 'content') {
            state.layout = 'settings';
          } else if (hashAction === 'appearance' && hashSection?.includes('custom-code')) {
            state.layout = 'settings-preview-stretch';
          } else if (hashAction === 'appearance') {
            state.layout = 'settings-preview';
          } else if (hashAction === 'ai') {
            state.layout = 'ai';
          } else if (hashAction === 'docs-audit') {
            state.layout = 'docs-audit';
          } else if (action === 'create') {
            state.layout = 'edit';
          } else if (action === 'update') {
            state.layout = 'edit';
          } else if (action === 'review') {
            state.layout = 'edit';
          } else {
            state.layout = 'default';
          }

          if (['settings', 'content', 'appearance', 'my-developers', 'ai'].includes(hashAction ?? '')) {
            // Always clear project store errors when re-routing.
            projectStore.getState().resetSaveError();
          }
        },
        false,
        actionLog('updateRoute', { pathname, hash }),
      );
    },
  };
});

/**
 * Bound react hook to access our SuperHub store. Must be called within a React
 * component. To access the store outside of React, use `superHubStore` instead.
 * @example
 * import { useSuperHubStore } from '@core/store';
 *
 * function Component() {
 *   const isEditing = useSuperHubStore(s => s.isEditing);
 * }
 */
export const useSuperHubStore = createBoundedUseStore(superHubStore);

export * from './ApiDefinitions';
export * from './Branches';
export * from './ConnectSuperHubStoreToRouter';
export * from './Document';
export * from './Editor';
export * from './InitializeSuperHubStore';
export * from './Sidebar';
export * from './Versions';
